import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { describeRule, type FormField } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { Alert, Box, Checkbox, Chip, InputAdornment, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '../../components/button';
import { TextField } from '../../components/text-field';
import { isEn, nameOf, tr } from '../../lib/i18n';
import { matches } from '../../lib/search';
import { isDisplay } from './helpers';
import { makeCtx, readTable, splitRule, writeTable } from './scenario';

interface Props {
  fields: FormField[];
  lookups?: Lookups;
  onChange: (next: FormField[]) => void;
  onOpen: (key: string) => void;
}

const label = (f: FormField) => (isEn() ? f.label.en : f.label.ar) || f.label.ar || f.key;

/**
 * Think like the customer: pick a request type, a category and one or more property types,
 * then tick the questions that case gets. Same rules underneath.
 */
export function ScenarioView({ fields, lookups, onChange, onOpen }: Props) {
  const [rt, setRt] = useState(() => lookups?.requestTypes[0]?.code ?? '');
  const [cat, setCat] = useState(() => lookups?.categories[0]?.code ?? '');
  const [types, setTypes] = useState<string[] | null>(null); // null = every type of the category
  const [q, setQ] = useState('');

  const catTypes = useMemo(() => {
    const c = lookups?.categories.find((x) => x.code === cat);
    return c ? (lookups?.propertyTypes ?? []).filter((t) => t.category_id === c.id) : [];
  }, [lookups, cat]);
  const chosen = types ?? catTypes.map((t) => t.code);

  const rows = useMemo(() => {
    if (!lookups) return [];
    return fields.map((f, i) => {
      if (isDisplay(f.type)) return { f, i, kind: 'section' as const };
      const c = makeCtx(fields, i, lookups);
      const { dim, rest } = splitRule(f.visibleWhen, c);
      const table = readTable(dim, c);
      const row = table[c.rtKey ? rt : ''] ?? {};
      const cells = c.ptKey ? chosen : [''];
      const on = cells.filter((p) => row[p]).length;
      return { f, i, kind: 'field' as const, c, rest, table, on, of: cells.length, fixed: !c.rtKey };
    });
  }, [fields, lookups, rt, chosen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lookups) return null;
  const hasRt = fields.some((f) => f.type === 'request_type');
  if (!hasRt) return <Alert severity="info">{tr('أضف حقل «نوع الطلب» أولاً.', 'Add a “Request type” field first.')}</Alert>;

  const toggle = (r: Extract<(typeof rows)[number], { kind: 'field' }>, on: boolean) => {
    const t = r.table;
    const key = r.c.rtKey ? rt : '';
    for (const p of r.c.ptKey ? chosen : ['']) t[key]![p] = on;
    const next = writeTable(t, r.rest, r.c);
    onChange(fields.map((x, j) => (j === r.i ? { ...x, visibleWhen: next } : x)));
  };

  const rtName = nameOf(lookups.requestTypes.find((x) => x.code === rt));
  const catName = nameOf(lookups.categories.find((x) => x.code === cat));
  const typeNames = types === null ? tr(`كل أنواع ${catName}`, `every ${catName} type`) : catTypes.filter((t) => chosen.includes(t.code)).map(nameOf).join('، ');
  const fieldRows = rows.filter((r) => r.kind === 'field');
  const shownCount = fieldRows.filter((r) => r.kind === 'field' && (r.fixed || r.on === r.of)).length;

  return (
    <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, flexShrink: 0 }}>
        <Step n={1} title={tr('نوع الطلب', 'Request type')}>
          {lookups.requestTypes.map((r) => <Pick key={r.code} label={nameOf(r)} on={rt === r.code} onClick={() => setRt(r.code)} />)}
        </Step>
        <Step n={2} title={tr('فئة العقار', 'Property category')}>
          {lookups.categories.map((c) => <Pick key={c.code} label={nameOf(c)} on={cat === c.code} onClick={() => { setCat(c.code); setTypes(null); }} />)}
        </Step>
        <Step n={3} title={tr('نوع العقار', 'Property type')} hint={tr('اختر نوعاً أو أكثر، والتعديل يُطبَّق عليهم معاً', 'Pick one or more; changes apply to all of them')}
          action={<Button size="small" onClick={() => setTypes(null)} disabled={types === null}>{tr('الكل', 'All')}</Button>}>
          {catTypes.map((t) => (
            <Pick key={t.code} label={nameOf(t)} on={chosen.includes(t.code)} soft={types === null}
              onClick={() => {
                const base = types ?? [];
                setTypes(base.includes(t.code) ? (base.length === 1 ? null : base.filter((x) => x !== t.code)) : [...base, t.code]);
              }} />
          ))}
          {!catTypes.length && <Typography variant="body2" color="text.secondary">{tr('لا توجد أنواع في هذه الفئة', 'No types in this category')}</Typography>}
        </Step>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
        <Typography fontWeight={700} sx={{ flex: 1, minWidth: 240 }}>
          {tr(`عميل يريد «${rtName}» ← ${catName} ← ${typeNames}: يُسأل ${shownCount} من ${fieldRows.length}`, `A customer who wants “${rtName}” → ${catName} → ${typeNames}: asked ${shownCount} of ${fieldRows.length}`)}
        </Typography>
        <TextField size="small" placeholder={tr('ابحث عن حقل…', 'Search fields…')} value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 220 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      </Stack>

      <Paper variant="outlined" sx={{ flex: 1, minHeight: { xs: 400, md: 0 }, overflowY: 'auto', borderRadius: 3, p: 1, columnCount: { xs: 1, lg: 2 }, columnGap: 3, '& > *': { breakInside: 'avoid' } }}>
        {rows.map((r) => {
          if (r.kind === 'section') {
            if (q) return null;
            return <Typography key={r.f.key} variant="subtitle2" fontWeight={700} sx={{ px: 1, pt: 1.5, pb: 0.5 }}>{label(r.f)}</Typography>;
          }
          if (!matches(q, r.f.label.ar, r.f.label.en, r.f.key)) return null;
          const all = r.on === r.of;
          const some = r.on > 0 && !all;
          const extra = r.rest.length > 0;
          return (
            <Stack key={r.f.key} direction="row" alignItems="center" spacing={1}
              sx={{ px: 1, py: 0.25, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' }, opacity: r.fixed ? 0.6 : 1 }}>
              <Tooltip title={r.fixed ? tr('قبل «نوع الطلب» — يظهر دائماً', 'Before “Request type” — always shown') : ''}>
                <span>
                  <Checkbox checked={r.fixed || all} indeterminate={some} disabled={r.fixed} onChange={() => toggle(r, !all)}
                    slotProps={{ input: { 'aria-label': label(r.f) } }} />
                </span>
              </Tooltip>
              <Box component="button" type="button" onClick={() => !r.fixed && toggle(r, !all)}
                sx={{ all: 'unset', cursor: r.fixed ? 'default' : 'pointer', flex: 1, minWidth: 0, py: 0.75 }}>
                <Typography variant="body2" fontWeight={all || r.fixed ? 600 : 400} noWrap>
                  {label(r.f)}{r.f.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                </Typography>
              </Box>
              {some && <Typography variant="caption" color="text.secondary">{tr(`${r.on} من ${r.of}`, `${r.on} of ${r.of}`)}</Typography>}
              {extra && (
                <Tooltip title={describeRule(r.rest.length === 1 ? r.rest[0]! : { all: r.rest }, (k) => label(fields.find((x) => x.key === k) ?? r.f))}>
                  <Chip size="small" variant="outlined" label={tr('+ شرط إضافي', '+ extra condition')} />
                </Tooltip>
              )}
              <Button size="small" onClick={() => onOpen(r.f.key)}>{tr('فتح', 'Open')}</Button>
            </Stack>
          );
        })}
      </Paper>
    </Stack>
  );
}

function Step({ n, title, hint, action, children }: { n: number; title: string; hint?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ py: 1, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { md: 230 }, flexShrink: 0 }}>
        <Box sx={{ width: 24, height: 24, flexShrink: 0, borderRadius: '50%', bgcolor: 'text.primary', color: 'background.paper', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700 }}>{n}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>{title}</Typography>
          {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{hint}</Typography>}
        </Box>
        {action}
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ flex: 1 }}>{children}</Stack>
    </Stack>
  );
}

function Pick({ label: l, on, soft, onClick }: { label: string; on: boolean; soft?: boolean; onClick: () => void }) {
  return (
    <Chip label={l} onClick={onClick} icon={on ? <CheckIcon /> : undefined} color={on ? 'primary' : 'default'} variant={on && !soft ? 'filled' : 'outlined'}
      sx={{ fontWeight: on ? 600 : 400 }} />
  );
}
